import React, { useEffect, useRef } from 'react';
import { Modal, ModalBody, ModalHeader, Button } from 'reactstrap';
import { AlertMessage } from 'utilities/utils';
import { ZapierExportPickerProps } from './types';
import { useZapierExport } from './useZapierExport';

const ZapierExportPicker: React.FC<ZapierExportPickerProps> = ({
  selectedRecords,
  cancelWidget,
}) => {
  const {
    isConfigured,
    isLoading,
    exportStatus,
    exportMessage,
    queuedCount,
    handleExport,
    resetExport,
  } = useZapierExport();

  const prevExportStatusRef = useRef<typeof exportStatus>(exportStatus);

  // Show toast notification when export status changes to error
  useEffect(() => {
    if (
      exportStatus === 'error' &&
      prevExportStatusRef.current !== 'error' &&
      exportMessage
    ) {
      AlertMessage('error', exportMessage, 4000);
    }
    prevExportStatusRef.current = exportStatus;
  }, [exportStatus, exportMessage]);

  const handleExportClick = async () => {
    const documentIds = selectedRecords.map((record) => record.uuid);
    try {
      await handleExport(documentIds);
    } catch (error) {
      // Error is already handled by the hook's internal error handling,
      // but we catch here as a safety net for any unexpected errors
      console.error('Unexpected error during Zapier export:', error);
    }
  };

  const handleClose = () => {
    resetExport();
    cancelWidget();
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="text-center py-4">
          <div
            id="zapier-config-loading"
            data-testid="loading"
            className="d-block"
            role="status"
            aria-label="Loading"
          />
          <p className="mt-2">Checking Zapier configuration...</p>
        </div>
      );
    }

    if (!isConfigured) {
      return (
        <div className="text-center py-4">
          <i
            className="icon-warning text-warning"
            style={{ fontSize: '48px' }}
          />
          <p className="mt-3">
            Zapier export is not configured. Please configure your Zapier
            webhook URL in the admin settings.
          </p>
        </div>
      );
    }

    if (exportStatus === 'exporting') {
      return (
        <div className="text-center py-4">
          <div
            id="zapier-export-loading"
            data-testid="loading"
            className="d-block"
            role="status"
            aria-label="Exporting"
          />
          <p className="mt-2">Exporting to Zapier...</p>
        </div>
      );
    }

    if (exportStatus === 'success') {
      return (
        <div className="text-center py-4">
          <i
            className="icon-check-circle text-success"
            style={{ fontSize: '48px' }}
          />
          <p className="mt-3 mb-0">{exportMessage}</p>
          <p className="text-muted">
            {queuedCount} document(s) queued for export
          </p>
        </div>
      );
    }

    if (exportStatus === 'error') {
      return (
        <div className="text-center py-4">
          <i
            className="icon-close-circle text-danger"
            style={{ fontSize: '48px' }}
          />
          <p className="mt-3 text-danger">{exportMessage}</p>
        </div>
      );
    }

    // Default: idle state - show export confirmation
    return (
      <div>
        <p>
          You are about to export{' '}
          <strong>{selectedRecords.length} document(s)</strong> to Zapier.
        </p>
        <p className="text-muted mb-0">
          The documents will be sent to your configured Zapier webhook for
          processing.
        </p>
      </div>
    );
  };

  const renderButtons = () => {
    if (isLoading) {
      return null;
    }

    if (exportStatus === 'success') {
      return (
        <div className="row">
          <div className="col-12 justify-content-end formbtn">
            <div className="btn-wrap btn--leftspace">
              <Button className="btn btn-primary" onClick={handleClose}>
                Done
              </Button>
            </div>
          </div>
        </div>
      );
    }

    if (exportStatus === 'error') {
      return (
        <div className="row">
          <div className="col-12 justify-content-end formbtn">
            <div className="btn-wrap btn--leftspace">
              <Button className="btn btn-outline-light" onClick={handleClose}>
                Close
              </Button>
              <Button
                className="btn btn-primary"
                onClick={handleExportClick}
                disabled={!isConfigured}
              >
                Retry
              </Button>
            </div>
          </div>
        </div>
      );
    }

    if (!isConfigured) {
      return (
        <div className="row">
          <div className="col-12 justify-content-end formbtn">
            <div className="btn-wrap btn--leftspace">
              <Button className="btn btn-outline-light" onClick={handleClose}>
                Close
              </Button>
            </div>
          </div>
        </div>
      );
    }

    // Default buttons for idle state
    return (
      <div className="row">
        <div className="col-12 justify-content-end formbtn">
          <div className="btn-wrap btn--leftspace">
            <Button
              className="btn btn-outline-light"
              onClick={handleClose}
              disabled={exportStatus === 'exporting'}
            >
              Cancel
            </Button>
            <Button
              className="btn btn-primary"
              onClick={handleExportClick}
              disabled={
                exportStatus === 'exporting' || selectedRecords.length === 0
              }
            >
              Export
            </Button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <Modal
      isOpen
      className="customModal customModal--center customModal--alert customModal--w600 customModal--nopadding"
    >
      <ModalHeader className="ModalHeader">
        <button
          type="button"
          onClick={handleClose}
          className="close"
          aria-label="Close"
          disabled={exportStatus === 'exporting'}
        >
          <i className="icon-close2" />
        </button>
      </ModalHeader>
      <ModalHeader className="modalHeader">
        <div className="text-medium pl-2">
          <div>Export to Zapier</div>
        </div>
      </ModalHeader>
      <ModalBody className="modalContent">
        <div className="modalContent--inner">
          {renderContent()}
          {renderButtons()}
        </div>
      </ModalBody>
    </Modal>
  );
};

export default ZapierExportPicker;
